import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react'
import { Logo } from '../components/Logo'

export function Contato() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormState('loading')

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://secbrain-tea.jfaeim.easypanel.host'
      const response = await fetch(`${apiUrl}/api/leads/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Falha ao enviar contato')
      }

      setFormState('success')
    } catch (error) {
      console.error('Error submitting contact:', error)
      setFormState('error')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="transition-transform duration-300 group-hover:scale-105">
              <Logo size={36} />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-900 font-semibold text-base leading-tight">SecBrain</span>
              <span className="text-teal-600 text-[9px] font-medium tracking-wide uppercase">Neurodivergente</span>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 lg:py-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight mb-4">
              Entre em <span className="text-gradient">Contato</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tem dúvidas, sugestões ou quer saber mais sobre o SecBrain?
              Ficaremos felizes em ajudar.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Informações de Contato
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">E-mail</h3>
                      <a
                        href="mailto:info@secbra.in"
                        className="text-teal-600 hover:underline"
                      >
                        info@secbra.in
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Localização</h3>
                      <p className="text-gray-600">Avenida Júlia Freire, 1200<br />Sala 109/110/111/112, Expedicionários<br />João Pessoa - PB, CEP 58041-000</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-medium text-gray-900 mb-3">Horário de Atendimento</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Segunda a Sexta: 9h às 18h<br />
                  (Horário de Brasília)
                </p>
                <p className="text-gray-500 text-sm">
                  Respondemos todas as mensagens em até 24 horas úteis.
                </p>
              </div>

              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
                <h3 className="font-medium text-gray-900 mb-3">Quer uma demonstração?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Se você quer ver o SecBrain em ação, agende uma demonstração
                  gratuita de 30 minutos.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center text-teal-600 text-sm font-medium hover:underline"
                >
                  Agendar demonstração →
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                {formState === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Mensagem enviada!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Obrigado pelo contato. Responderemos em breve.
                    </p>
                    <button
                      onClick={() => {
                        setFormState('idle')
                        setFormData({ name: '', email: '', subject: '', message: '' })
                      }}
                      className="text-teal-600 font-medium hover:underline"
                    >
                      Enviar outra mensagem
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Nome *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all"
                          placeholder="Seu nome"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          E-mail *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all"
                          placeholder="seu@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Assunto *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all bg-white"
                      >
                        <option value="">Selecione um assunto</option>
                        <option value="comercial">Informações comerciais</option>
                        <option value="suporte">Suporte técnico</option>
                        <option value="parceria">Parcerias</option>
                        <option value="imprensa">Imprensa</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Mensagem *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all resize-none"
                        placeholder="Como podemos ajudar?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formState === 'loading'}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formState === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Enviar mensagem
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} SecBrain Neurodivergente. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
